import { StopRecord, Meter } from '../types';
import OpenAI from 'openai';

export interface AIAnalysisResult {
    summary: string;
    insights: string[];
    isAnomaly: boolean;
    cargaBaseScore: number; // 0-100 score on how close average is to the ideal base load
}

const generateSimulatedAnalysis = async (records: StopRecord[], meters: Meter[]): Promise<AIAnalysisResult> => {
    // Lógica local (Simulada) pre-existente
    await new Promise(resolve => setTimeout(resolve, 1000));

    const completeRecords = records.filter(r => r.endValue !== undefined && r.endTime !== undefined);

    // ... Calcular consumo por hora de cada registro
    const hourlyRates = completeRecords.map(record => {
        const meter = meters.find(m => m.id === record.meterId);
        const consumoTotal = record.endValue! - record.startValue;
        const horas = (record.endTime! - record.startTime) / (1000 * 60 * 60);
        const porHora = horas > 0 ? consumoTotal / horas : 0;

        return {
            id: record.id,
            reason: record.reason,
            date: new Date(record.startTime).toLocaleDateString(),
            meterName: meter?.name || 'Desconocido',
            unit: meter?.unit || 'uds',
            total: consumoTotal,
            horas: horas,
            porHora: porHora
        };
    });

    const rates = hourlyRates.map(r => r.porHora).filter(r => !isNaN(r));
    const avgRate = rates.length > 0 ? rates.reduce((a, b) => a + b, 0) / rates.length : 0;

    const insights: string[] = [];
    let isAnomaly = false;
    let cargaBaseScore = 100;

    if (completeRecords.length === 1) {
        const r = hourlyRates[0];
        return {
            summary: `Evaluación inicial de Carga Base para ${r.meterName}.`,
            insights: [
                `Durante esta parada de ${r.horas.toFixed(2)} horas, el consumo total fue ${r.total.toLocaleString()} ${r.unit}.`,
                `Esto establece una Carga Base observada de ${r.porHora.toFixed(2)} ${r.unit}/hora.`,
                `💡 Selecciona múltiples registros de paradas similares para confirmar si este es el límite inferior (Carga Base ideal).`
            ],
            isAnomaly: false,
            cargaBaseScore: 80
        };
    }

    const maxRate = Math.max(...rates);
    const minRate = Math.min(...rates);
    const variance = maxRate > 0 ? ((maxRate - minRate) / maxRate) * 100 : 0;

    const highestConsumer = hourlyRates.find(r => r.porHora === maxRate);
    const lowestConsumer = hourlyRates.find(r => r.porHora === minRate);
    const isMixedUnits = new Set(hourlyRates.map(r => r.unit)).size > 1;

    let summary = "";

    if (isMixedUnits) {
        summary = "Análisis detectó múltiples unidades de medida (Agua y Energía mixtas).";
        insights.push("Para determinar la Carga Base correctamente, selecciona medidores del mismo tipo (solo agua o solo electricidad).");
        insights.push(`Sin embargo, se detectó que el consumo máximo por hora fue de ${highestConsumer?.porHora.toFixed(2)} ${highestConsumer?.unit}/h el ${highestConsumer?.date}.`);
        cargaBaseScore = 50;
    } else {
        const unit = hourlyRates[0].unit;
        if (variance > 40) {
            summary = "⚠️ Alerta de Consumo: Alta variabilidad. Es difícil establecer una Carga Base estable (Diferencia > 40%).";
            isAnomaly = true;
            cargaBaseScore = Math.max(10, 100 - variance);
            insights.push(`La Carga Base MÍNIMA alcanzada en estos eventos es de ${lowestConsumer?.porHora.toFixed(2)} ${unit}/h (ocurrió el ${lowestConsumer?.date}). Este debería ser tu objetivo para todas las paradas.`);
            insights.push(`El mayor pico de consumo (fuga/ineficiencia) fue el ${highestConsumer?.date} con ${highestConsumer?.porHora.toFixed(2)} ${unit}/h.`);
            insights.push(`💡 Sugerencia: El consumo en vacío debería ser cercano a ${lowestConsumer?.porHora.toFixed(2)} ${unit}/h. Revisa los procedimientos de apagado del evento pico, es probable que equipos auxiliares hayan quedado encendidos.`);
        } else if (variance > 15) {
            summary = "Evaluación de Carga Base: Variaciones moderadas detectadas durante los paros.";
            cargaBaseScore = 80 - (variance / 2);
            isAnomaly = false;
            insights.push(`El límite inferior de consumo (Carga Base teórica) se sitúa en ${lowestConsumer?.porHora.toFixed(2)} ${unit}/h.`);
            insights.push(`El consumo promedio de los eventos seleccionados es de ${avgRate.toFixed(2)} ${unit}/h.`);
            insights.push(`💡 Aún existe oportunidad de ahorro; intente replicar las condiciones de cierre del evento del ${lowestConsumer?.date} en las siguientes paradas.`);
        } else {
            summary = "✅ Carga Base Identificada y Estable: Los paros técnicos tienen un perfil de consumo consistente.";
            cargaBaseScore = 95;
            insights.push(`El límite mínimo detectado es de ${lowestConsumer?.porHora.toFixed(2)} ${unit}/h.`);
            insights.push(`Todos los eventos evaluados rondan los ${avgRate.toFixed(2)} ${unit}/h con mínima desviación.`);
            insights.push(`Esto confirma que la Carga Base para este grupo es de aproximadamente ${avgRate.toFixed(2)} ${unit}/h y los protocolos de detención se aplican correctamente.`);
        }
    }

    return { summary, insights, isAnomaly, cargaBaseScore };
};

export const generateAIAnalysis = async (
    records: StopRecord[],
    meters: Meter[]
): Promise<AIAnalysisResult> => {

    if (!records || records.length === 0) {
        return {
            summary: "No hay suficientes datos seleccionados para analizar la carga base.",
            insights: ["Selecciona uno o más registros en la tabla para comparar el consumo."],
            isAnomaly: false,
            cargaBaseScore: 0
        };
    }

    const completeRecords = records.filter(r => r.endValue !== undefined && r.endTime !== undefined);

    if (completeRecords.length === 0) {
        return {
            summary: "Los registros seleccionados aún no han sido finalizados.",
            insights: ["El análisis requiere paradas con consumo final registrado para determinar la carga base."],
            isAnomaly: false,
            cargaBaseScore: 0
        };
    }

    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;

    // Si NO hay API Key configurada, usar el algoritmo local simulado
    if (!apiKey || apiKey.trim() === '' || apiKey.includes('pega_aqui')) {
        console.log("No OpenAI API Key found or placeholder detected. Falling back to local simulated rules.");
        return generateSimulatedAnalysis(records, meters);
    }

    try {
        console.log("OpenAI API Key provided. Generating smart insights...");

        const openai = new OpenAI({
            apiKey: apiKey,
            dangerouslyAllowBrowser: true // Necesario para ejecutar desde el cliente de Vite
        });

        // Preparar payload de datos para la IA
        const mappedData = completeRecords.map(record => {
            const meter = meters.find(m => m.id === record.meterId);
            const consumoTotal = record.endValue! - record.startValue;
            const horas = (record.endTime! - record.startTime) / (1000 * 60 * 60);
            return {
                maquina: meter?.name || 'Desconocido',
                unidad: meter?.unit || 'uds',
                motivoParada: record.reason,
                duracionHoras: Number(horas.toFixed(2)),
                consumoTotal: Number(consumoTotal.toFixed(2)),
                consumoPorHora: horas > 0 ? Number((consumoTotal / horas).toFixed(2)) : 0,
                fechaInicio: new Date(record.startTime).toISOString(),
            };
        });

        const systemPrompt = `Eres un ingeniero experto en eficiencia energética industrial y evaluación de 'carga base'.
El usuario te enviará un array JSON con registros de paradas técnicas (donde las máquinas deberían estar apagadas consumiendo lo mínimo vital).
Tu objetivo es analizar el 'consumoPorHora' para detectar ineficiencias, fugas, o equipos auxiliares dejados encendidos.
Debes devolver tu análisis OBLIGATORIAMENTE en el siguiente formato JSON puro, sin markdown ni comillas invertidas:
{
  "summary": "Un resumen de una línea de tu conclusión general.",
  "insights": ["Detalle 1 explicativo", "Detalle 2 con recomendación accionable", "Detalle 3 comparativo"],
  "isAnomaly": true o false (si la variación de consumos por hora es extraña o demasiado alta),
  "cargaBaseScore": un número del 0 al 100 calificando qué tan estable es el consumo de carga base (100 = perfecto y constante).
}`;

        const response = await openai.chat.completions.create({
            model: "gpt-4o-mini", // Modelo económico y veloz
            messages: [
                { role: "system", content: systemPrompt },
                { role: "user", content: "Evalúa estos registros:\n" + JSON.stringify(mappedData, null, 2) }
            ],
            response_format: { type: "json_object" },
            temperature: 0.3,
        });

        const resultJsonString = response.choices[0].message.content;

        if (!resultJsonString) throw new Error("API devolvió una respuesta vacía");

        const parsedResult = JSON.parse(resultJsonString) as AIAnalysisResult;
        return parsedResult;

    } catch (error) {
        console.error("OpenAI Fallback Triggered. Error calling OpenAI API:", error);
        // Si la llamada falla (límites de tokens, clave inválida, red cortada), hacer fallback al algoritmo local de rescate.
        return generateSimulatedAnalysis(records, meters);
    }
};
